import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useUserContext } from 'app/services/UserProvider/UserProvider'
import tagManager from 'app/tagManager'
import { ROOT } from 'app/thread'
import Form from 'components/core/Form/Form'
import Layout from 'components/core/Layout/Layout'
import GradientText from 'components/elements/GradientText/GradientText'
import SimpleModal from 'components/elements/SimpleModal/SimpleModal'
import Spacer from 'components/elements/Spacer/Spacer'
import PrivacyTermsDialog from 'components/interface/PrivacyTermsDialog/PrivacyTermsDialog'
import {
  validateChecked,
  validateEmail,
  validateString
} from 'lib/util/validators'
import styles from './PromptSignUp.module.scss'

/**
 * The `PromptSignUp` component. Used for when the
 * user is NOT a passport holder AND has not signed up.
 *
 * @returns {React.ReactElement} the element
 */
function PromptSignUp () {
  const { account, logoutUser, signUpStandardUser, userActivityStatus } =
    useUserContext()
  const [errorMessage, setErrorMessage] = useState('')
  const [isTermsOpen, setIsTermsOpen] = useState(false)
  const firstFieldRef = useRef()
  const providedEmail = useMemo(
    () => account?.wallet?.authDetails?.email,
    [account]
  )

  /**
   * Handle click logout user
   */
  const handleClickDisconnectWallet = useCallback(
    e => {
      e.preventDefault()
      e.stopPropagation()

      logoutUser()
    },
    [logoutUser]
  )

  /**
   * Handle submit
   */
  const handleSubmit = useCallback(
    async (e, formData) => {
      if (userActivityStatus) {
        // Block when busy
        return
      }

      const email = providedEmail || formData.email
      const res = await signUpStandardUser({
        ...formData,
        email
      })

      if (!res?.success) {
        // Fail
        setErrorMessage(res.message)

        tagManager.trackEvent({
          event: 'signup.error'
        })

        return
      }

      tagManager.trackEvent({
        event: 'signup.success'
      })
    },
    [userActivityStatus, providedEmail]
  )

  /**
   * Handle before submit
   */
  const handleBeforeSubmit = useCallback(() => {
    setErrorMessage('')
  }, [])

  /**
   * Handle click the terms
   */
  const handleClickTerms = useCallback(e => {
    e.preventDefault()
    e.stopPropagation()
    setIsTermsOpen(true)
  }, [])

  /**
   * Handle close the terms
   */
  const handleCloseTerms = useCallback(e => {
    e.preventDefault()
    e.stopPropagation()
    setIsTermsOpen(false)
  }, [])

  /**
   * Effect: On mount
   */
  useEffect(() => {
    if (firstFieldRef?.current) {
      // Focus the first input field
      firstFieldRef.current.focus()
    }
  }, [])

  return (
    <>
      <Layout.Body align="center">
        <p className={styles.title}>
          To get started, please enter your{' '}
          <GradientText Tag="span">
            Name{providedEmail ? '' : '& Email'}.
          </GradientText>
        </p>
      </Layout.Body>
      <Form
        className={styles.form}
        isBusy={!!userActivityStatus}
        onBeforeSubmit={handleBeforeSubmit}
        onSubmit={handleSubmit}
        errorMessage={errorMessage}
      >
        <Form.Group display="inline">
          <Form.InputField
            ref={firstFieldRef}
            label="First Name"
            name="firstName"
            validate={validateString}
          />
          <Form.InputField
            label="Last Name"
            name="lastName"
            validate={validateString}
          />
        </Form.Group>

        {!providedEmail && (
          <Form.Group display="inline">
            <Form.InputField
              label="Email"
              name="email"
              type="email"
              validate={validateEmail}
            />
          </Form.Group>
        )}
        <Form.Group display="inline">
          <Form.InputField label="Referral Code" name="referralCode" />
        </Form.Group>
        <Spacer size="x-small" />
        <Form.Group>
          <Form.Checkbox
            label={
              <>
                <strong style={{ color: 'var(--color-d-accent-a)' }}>
                  Sign up to our Newsletter
                </strong>{' '}
                — be the first to know about exciting developments & exclusive
                offers!
              </>
            }
            name="marketing"
            value={true}
          />
        </Form.Group>
        <Form.Group>
          <Form.Checkbox
            label={
              <span>
                Please check the box to indicate that you have read and agree to
                our{' '}
                <button className={styles.button} onClick={handleClickTerms}>
                  Terms & Conditions and Privacy Policy
                </button>{' '}
                before proceeding with the registration.
              </span>
            }
            validate={validateChecked}
            validationErrorMessage="Please accept our Terms & Conditions and Privacy Policy."
            name="accept"
          />
        </Form.Group>
        <Form.Group>
          <Form.Submit size="inherit">Submit</Form.Submit>
        </Form.Group>
      </Form>
      {errorMessage && (
        <div className={styles.disconnect}>
          <button
            className={styles.button}
            onClick={handleClickDisconnectWallet}
          >
            Issues? Disconnect wallet
          </button>
        </div>
      )}
      {!errorMessage && (
        <div className={styles.disconnect}>
          <button
            className={styles.button}
            onClick={handleClickDisconnectWallet}
          >
            Switching accounts? Disconnect wallet
          </button>
        </div>
      )}
      {isTermsOpen &&
        createPortal(
          <SimpleModal size="large" color="b" onClose={handleCloseTerms}>
            <PrivacyTermsDialog />
          </SimpleModal>,
          ROOT
        )}
    </>
  )
}

// Memoize
export default memo(PromptSignUp)
